import { useState } from "react";
import { api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Camera, Loader2, Users, ArrowRight, Plus, RefreshCw, UserCheck, UserPlus,
} from "lucide-react";

const BusinessCardScanDialog = ({ open, onOpenChange, customers, onComplete }) => {
  const [scanning, setScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanFile, setScanFile] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [processing, setProcessing] = useState(false);

  const reset = () => {
    setScanning(false);
    setScanPreview(null);
    setScanResult(null);
    setScanFile(null);
    setSelectedAction(null);
    setSelectedCustomerId("");
    setProcessing(false);
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setScanning(true);
    setScanPreview(URL.createObjectURL(file));
    setScanFile(file);
    setScanResult(null);
    setSelectedAction(null);

    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(file);
      });

      const res = await api.post("/api/customers/scan-business-card", {
        image_base64: base64,
        ocr_type: "business_card",
      });

      if (res.data.success) {
        setScanResult(res.data);
        toast.success("Visitenkarte erkannt!");
      } else {
        toast.error(res.data.error || "Scannen fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Fehler beim Scannen");
      console.error(err);
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  };

  const saveDocument = async (customerId) => {
    if (!scanFile || !customerId) return;
    try {
      const form = new FormData();
      form.append("file", scanFile);
      form.append("doc_type", "visitenkarte");
      await api.post(`/api/customers/${customerId}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (err) {
      console.error("Document save failed:", err);
    }
  };

  const handleAction = async (action) => {
    if (!scanResult) return;
    setProcessing(true);

    const ocr = scanResult.ocr_data;
    const match = scanResult.customer_matches?.[0];

    try {
      if (action === "update_existing" && match) {
        // Update existing customer with scanned data, only fill empty fields
        const updateData = {};
        if (!match.phone && ocr.phone) updateData.phone = ocr.phone;
        if (!match.email && ocr.email) updateData.email = ocr.email;
        if (!match.street && ocr.street) updateData.street = ocr.street;
        if (!match.postal_code && ocr.postal_code) updateData.postal_code = ocr.postal_code;
        if (!match.city && ocr.city) updateData.city = ocr.city;

        // Add company/position to notes if present
        const noteParts = [];
        if (ocr.company) noteParts.push(`Firma: ${ocr.company}`);
        if (ocr.position) noteParts.push(`Position: ${ocr.position}`);
        if (ocr.website) noteParts.push(`Web: ${ocr.website}`);
        if (noteParts.length > 0) {
          const existing = match.notes || "";
          const newNote = noteParts.join(", ");
          if (!existing.includes(newNote)) {
            updateData.notes = existing ? `${existing}\n${newNote}` : newNote;
          }
        }

        if (Object.keys(updateData).length > 0) {
          await api.put(`/api/customers/${match.id}`, updateData);
        }
        await saveDocument(match.id);
        toast.success(`${match.first_name} ${match.last_name} aktualisiert`);
        handleClose(false);
        onComplete?.();

      } else if (action === "overwrite_existing" && match) {
        // Overwrite existing customer with all scanned data
        const updateData = {};
        if (ocr.first_name) updateData.first_name = ocr.first_name;
        if (ocr.last_name) updateData.last_name = ocr.last_name;
        if (ocr.phone) updateData.phone = ocr.phone;
        if (ocr.email) updateData.email = ocr.email;
        if (ocr.street) updateData.street = ocr.street;
        if (ocr.postal_code) updateData.postal_code = ocr.postal_code;
        if (ocr.city) updateData.city = ocr.city;

        const noteParts = [];
        if (ocr.company) noteParts.push(`Firma: ${ocr.company}`);
        if (ocr.position) noteParts.push(`Position: ${ocr.position}`);
        if (ocr.website) noteParts.push(`Web: ${ocr.website}`);
        if (noteParts.length > 0) updateData.notes = noteParts.join(", ");

        await api.put(`/api/customers/${match.id}`, updateData);
        await saveDocument(match.id);
        toast.success(`${ocr.first_name} ${ocr.last_name} komplett aktualisiert`);
        handleClose(false);
        onComplete?.();

      } else if (action === "create_new") {
        // Create new customer from scanned data
        const noteParts = [];
        if (ocr.company) noteParts.push(`Firma: ${ocr.company}`);
        if (ocr.position) noteParts.push(`Position: ${ocr.position}`);
        if (ocr.website) noteParts.push(`Web: ${ocr.website}`);

        const newCustomer = await api.post("/api/customers", {
          first_name: ocr.first_name || "",
          last_name: ocr.last_name || "",
          phone: ocr.phone || "",
          email: ocr.email || "",
          street: ocr.street || "",
          postal_code: ocr.postal_code || "",
          city: ocr.city || "",
          notes: noteParts.join(", "),
        });

        await saveDocument(newCustomer.data.id);
        toast.success(`Neuer Kunde ${ocr.first_name} ${ocr.last_name} angelegt`);
        handleClose(false);
        onComplete?.();

      } else if (action === "fill_form") {
        // Just fill the form with scanned data (don't save yet)
        handleClose(false);
        onComplete?.("fill_form", ocr);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Verarbeitung");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="business-card-scan-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Barlow Condensed" }} className="text-xl">
            Visitenkarte scannen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Upload Area */}
          {!scanResult && (
            <label className="cursor-pointer block border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
              {scanning ? (
                <div className="flex flex-col items-center gap-3 py-4">
                  {scanPreview && <img src={scanPreview} alt="Scan" className="max-h-40 rounded object-contain" />}
                  <div className="flex items-center gap-2 text-primary">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="font-medium">Wird analysiert...</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 py-4">
                  <Camera className="w-10 h-10 text-muted-foreground" />
                  <p className="font-medium">Visitenkarte fotografieren oder hochladen</p>
                  <p className="text-sm text-muted-foreground">Kontaktdaten werden automatisch erkannt und abgeglichen</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleScan}
                disabled={scanning}
                data-testid="business-card-scan-input"
              />
            </label>
          )}

          {/* Scan Results */}
          {scanResult && (
            <div className="space-y-4">
              {/* Preview */}
              {scanPreview && (
                <div className="flex justify-center">
                  <img src={scanPreview} alt="Visitenkarte" className="max-h-32 rounded shadow object-contain" />
                </div>
              )}

              {/* Erkannte Daten */}
              <Card data-testid="card-scan-results">
                <CardContent className="p-4 space-y-3">
                  <p className="font-semibold text-sm">Erkannte Kontaktdaten:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {scanResult.ocr_data.first_name && (
                      <div><span className="text-muted-foreground">Vorname:</span> <strong>{scanResult.ocr_data.first_name}</strong></div>
                    )}
                    {scanResult.ocr_data.last_name && (
                      <div><span className="text-muted-foreground">Nachname:</span> <strong>{scanResult.ocr_data.last_name}</strong></div>
                    )}
                    {scanResult.ocr_data.company && (
                      <div className="col-span-2"><span className="text-muted-foreground">Firma:</span> <strong>{scanResult.ocr_data.company}</strong></div>
                    )}
                    {scanResult.ocr_data.position && (
                      <div className="col-span-2"><span className="text-muted-foreground">Position:</span> <strong>{scanResult.ocr_data.position}</strong></div>
                    )}
                    {scanResult.ocr_data.phone && (
                      <div><span className="text-muted-foreground">Telefon:</span> <strong>{scanResult.ocr_data.phone}</strong></div>
                    )}
                    {scanResult.ocr_data.email && (
                      <div><span className="text-muted-foreground">E-Mail:</span> <strong>{scanResult.ocr_data.email}</strong></div>
                    )}
                    {scanResult.ocr_data.street && (
                      <div className="col-span-2"><span className="text-muted-foreground">Adresse:</span> <strong>{scanResult.ocr_data.street}, {scanResult.ocr_data.postal_code} {scanResult.ocr_data.city}</strong></div>
                    )}
                    {scanResult.ocr_data.website && (
                      <div className="col-span-2"><span className="text-muted-foreground">Web:</span> <strong>{scanResult.ocr_data.website}</strong></div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Match found */}
              {!scanResult.is_new_customer && scanResult.customer_matches?.length > 0 && (
                <Card className="border-blue-200 bg-blue-50/50" data-testid="customer-match-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-5 h-5 text-blue-600" />
                      <p className="font-semibold text-sm">Kunde bereits vorhanden</p>
                      <Badge variant="outline" className="ml-auto text-xs">
                        {scanResult.customer_matches.length} Treffer
                      </Badge>
                    </div>
                    <p className="text-sm">
                      <strong>{scanResult.customer_matches[0].first_name} {scanResult.customer_matches[0].last_name}</strong>
                      {scanResult.customer_matches[0].phone && (
                        <span className="text-muted-foreground"> - {scanResult.customer_matches[0].phone}</span>
                      )}
                    </p>

                    <div className="space-y-2 pt-2">
                      <Button
                        variant={selectedAction === "update_existing" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("update_existing")}
                        data-testid="action-update-existing"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Fehlende Daten ergänzen
                      </Button>
                      <Button
                        variant={selectedAction === "overwrite_existing" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("overwrite_existing")}
                        data-testid="action-overwrite-existing"
                      >
                        <UserCheck className="w-4 h-4" />
                        Alle Daten überschreiben
                      </Button>
                      <Button
                        variant={selectedAction === "create_new" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("create_new")}
                        data-testid="action-create-anyway"
                      >
                        <UserPlus className="w-4 h-4" />
                        Trotzdem als neuen Kunden anlegen
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* No match - new customer */}
              {scanResult.is_new_customer && (
                <Card className="border-green-200 bg-green-50/50" data-testid="new-customer-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Plus className="w-5 h-5 text-green-600" />
                      <p className="font-semibold text-sm">Neuer Kontakt erkannt</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Kein passender Kunde in der Datenbank gefunden.
                    </p>
                    <div className="space-y-2 pt-2">
                      <Button
                        variant={selectedAction === "create_new" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("create_new")}
                        data-testid="action-create-new"
                      >
                        <UserPlus className="w-4 h-4" />
                        Neuen Kunden anlegen
                      </Button>
                      <Button
                        variant={selectedAction === "fill_form" ? "default" : "outline"}
                        className="w-full justify-start gap-2"
                        onClick={() => setSelectedAction("fill_form")}
                        data-testid="action-fill-form"
                      >
                        <Users className="w-4 h-4" />
                        Daten ins Formular übernehmen (manuell prüfen)
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Confirm */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => reset()} className="flex-1" data-testid="card-scan-reset">
                  Erneut scannen
                </Button>
                <Button
                  onClick={() => handleAction(selectedAction)}
                  disabled={!selectedAction || processing}
                  className="flex-1 btn-primary gap-2"
                  data-testid="card-scan-confirm"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  Bestätigen
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessCardScanDialog;
